/**
 * Zustand store for ephemeral admin-UI state — modal open flags and in-flight
 * role-editor drafts. Server state lives in react-query (see
 * usePermissionsAdmin.js).
 *
 * User/membership management (including invitations) is handled by Clerk's
 * native OrganizationProfile "Members" tab, so this store no longer tracks any
 * user- or invitation-related UI state.
 */

import { create } from 'zustand';

const usePermissionsAdminStore = create((set, get) => ({
  // ---- Role-editor draft state ----
  // The draft is keyed by BARE permission slugs (`resource:action`), matching
  // what the backend stores and returns post-Mongo-pivot. Keys that arrive
  // from the backend with a legacy `org:` prefix are normalised on the way
  // in; the matrix grid + save path both operate on bare slugs.
  editorDraft: null,
  editorDirty: false,
  editorRoleSlug: null,

  initEditor: (slug, permissions) => {
    const draft = {};
    for (const p of permissions || []) {
      const bare = typeof p === 'string' && p.startsWith('org:') ? p.slice(4) : p;
      if (bare) draft[bare] = true;
    }
    set({ editorRoleSlug: slug, editorDraft: draft, editorDirty: false });
  },

  toggleGrant: (resource, action) => {
    const draft = get().editorDraft || {};
    const key = `${resource}:${action}`;
    const next = { ...draft };
    if (next[key]) delete next[key];
    else next[key] = true;
    set({ editorDraft: next, editorDirty: true });
  },

  setResourceActions: (resource, actions) => {
    const draft = { ...(get().editorDraft || {}) };
    for (const action of ['read', 'create', 'edit', 'delete']) {
      delete draft[`${resource}:${action}`];
    }
    for (const action of actions) {
      draft[`${resource}:${action}`] = true;
    }
    set({ editorDraft: draft, editorDirty: true });
  },

  replaceDraft: (permissions) => {
    const draft = {};
    for (const p of permissions || []) {
      const bare = typeof p === 'string' && p.startsWith('org:') ? p.slice(4) : p;
      if (bare) draft[bare] = true;
    }
    set({ editorDraft: draft, editorDirty: true });
  },

  resetEditor: () => set({ editorDraft: null, editorDirty: false, editorRoleSlug: null }),

  markEditorClean: () => set({ editorDirty: false }),

  // ---- Org-profile inline role editor (Clerk modal) ----
  // Separate from editorRoleSlug so the removed /settings/permissions/*
  // page routes can't hijack the modal's list/editor toggle.
  orgProfileEditingRoleSlug: null,
  setOrgProfileEditingRoleSlug: (slug) => set({ orgProfileEditingRoleSlug: slug }),
}));

export default usePermissionsAdminStore;
